import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Input } from "../ui/input";
import { Archive, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import DishesGrid from "./DishesGrid";

/**
 * Full-screen dialog that lists archived dishes (recipes or beverages) of a
 * restaurant. From each card the user can Restore or Permanently delete.
 *
 * Props:
 *   open / onOpenChange   — Dialog visibility
 *   restaurantId          — to fetch menus
 *   isBeverage            — true for the Bebidas tab (filters dishes)
 *   onChanged             — called after restore/delete so parent refreshes
 */
export default function ArchivedDishesDialog({
  open,
  onOpenChange,
  restaurantId,
  isBeverage = false,
  onChanged,
}) {
  const [loading, setLoading] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const BEV_CAT_KEYWORDS = useMemo(() => [
    "bebida", "coctel", "cóctel", "refresco", "vino",
    "cerveza", "café", "cafe", "té ", "licor", "carta de vinos", "bodega",
  ], []);

  const fetchArchived = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const menus = await api.getMenus(restaurantId);
      const all = [];
      const cats = {};
      for (const m of menus) {
        try {
          const [ds, cs] = await Promise.all([
            api.getDishes(m.menu_id, { archived: true }),
            api.getCategories(m.menu_id),
          ]);
          cs.forEach((c) => { cats[c.category_id] = c; });
          all.push(...ds);
        } catch { /* skip */ }
      }
      let filtered = all;
      if (isBeverage) {
        const bevCatIds = new Set(
          Object.values(cats)
            .filter((c) => {
              const n = (c.name || "").toLowerCase();
              return BEV_CAT_KEYWORDS.some((k) => n.includes(k));
            })
            .map((c) => c.category_id),
        );
        filtered = all
          .filter((d) =>
            d.source === "universal_beverage"
            || (d.tags || []).includes("bebida")
            || bevCatIds.has(d.category_id)
            || d.is_commercial_beverage === true,
          )
          .map((d) => ({ ...d, _isBeverage: true }));
      } else {
        // Recetas tab: exclude beverages
        const bevCatIds = new Set(
          Object.values(cats)
            .filter((c) => {
              const n = (c.name || "").toLowerCase();
              return BEV_CAT_KEYWORDS.some((k) => n.includes(k));
            })
            .map((c) => c.category_id),
        );
        filtered = all.filter((d) =>
          d.source !== "universal_beverage"
          && !(d.tags || []).includes("bebida")
          && !bevCatIds.has(d.category_id)
          && d.is_commercial_beverage !== true,
        );
      }
      setDishes(filtered);
    } catch {
      toast.error("No se pudieron cargar las archivadas");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, isBeverage, BEV_CAT_KEYWORDS]);

  useEffect(() => {
    if (open) {
      setSearch("");
      fetchArchived();
    }
  }, [open, fetchArchived]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dishes;
    return dishes.filter((d) => (d.name || "").toLowerCase().includes(q));
  }, [dishes, search]);

  const handleRestore = async (dish) => {
    setBusyId(dish.dish_id);
    try {
      await api.restoreDish(dish.dish_id);
      setDishes((prev) => prev.filter((d) => d.dish_id !== dish.dish_id));
      toast.success(`"${dish.name}" restaurada`);
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "No se pudo restaurar");
    } finally {
      setBusyId(null);
    }
  };

  const handleDeletePermanent = async () => {
    const dish = confirmDelete;
    if (!dish) return;
    setBusyId(dish.dish_id);
    try {
      await api.deleteDishPermanent(dish.dish_id);
      setDishes((prev) => prev.filter((d) => d.dish_id !== dish.dish_id));
      toast.success(`"${dish.name}" eliminada definitivamente`);
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "No se pudo eliminar");
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  const label = isBeverage ? "bebidas" : "recetas";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-6xl max-h-[90vh] flex flex-col"
          data-testid="archived-dishes-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-muted-foreground" />
              {isBeverage ? "Bebidas archivadas" : "Recetas archivadas"}
            </DialogTitle>
            <DialogDescription>
              Puedes restaurar una {label.slice(0, -1)} para que vuelva al grid principal
              o eliminarla definitivamente. La eliminación definitiva es irreversible.
            </DialogDescription>
          </DialogHeader>

          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Buscar entre ${dishes.length} ${label} archivadas…`}
              className="pl-9"
              data-testid="archived-search-input"
            />
          </div>

          <div className="flex-1 overflow-auto pr-1 mt-3">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : dishes.length === 0 ? (
              <div className="text-center py-20">
                <Archive className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No hay {label} archivadas.
                </p>
              </div>
            ) : (
              <DishesGrid
                dishes={dishes}
                filteredDishes={visible}
                searchQuery={search}
                onRestore={handleRestore}
                onDelete={(d) => setConfirmDelete(d)}
                onClearFilters={() => setSearch("")}
                isArchivedView
                busyId={busyId}
                emptyTitle={`Sin ${label} archivadas`}
                emptyDescription=""
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}
      >
        <AlertDialogContent data-testid="confirm-delete-permanent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar <strong>«{confirmDelete?.name}»</strong> de forma
              permanente. Se perderá la receta, los ingredientes vinculados y la
              imagen generada por IA. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-permanent">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={handleDeletePermanent}
              data-testid="confirm-delete-permanent"
            >
              Eliminar definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
